import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/api";

const EMPTY = { name: "", email: "", phone: "", travel_date: "", travelers: 2, message: "" };

export default function EnquiryDrawer({ open, onClose, pkg, packages }) {
  const [form, setForm] = useState(EMPTY);
  const [packageId, setPackageId] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const selectedPkg = pkg || packages.find((p) => p.id === packageId) || null;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/enquiries", {
        ...form,
        travelers: Number(form.travelers),
        package_id: selectedPkg?.id || null,
        package_name: selectedPkg?.name || null,
        destination: selectedPkg?.destination || null,
      });
      toast.success("Enquiry received! Your travel designer will call you within 24 hours.");
      setForm(EMPTY);
      setPackageId("");
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[100]" data-testid="enquiry-drawer">
          <motion.div className="absolute inset-0 bg-onyx-900/70 backdrop-blur-sm"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} />
          <motion.aside
            className="absolute right-0 top-0 h-full w-full max-w-md bg-onyx-800 border-l border-white/10 shadow-2xl flex flex-col"
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}>
            <div className="flex items-start justify-between p-6 border-b border-white/10">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Trip Enquiry</div>
                <h3 className="mt-1 font-serif text-2xl text-slate-50">Design my journey</h3>
                {selectedPkg && <p className="mt-1 text-xs text-slate-400">{selectedPkg.name}</p>}
              </div>
              <button onClick={onClose} data-testid="enquiry-close-btn" aria-label="Close"
                className="rounded-full border border-white/15 p-2 text-slate-300 hover:text-gold hover:border-gold/50 transition-colors">
                <X size={16} />
              </button>
            </div>

            <form onSubmit={submit} className="flex-1 overflow-y-auto p-6 space-y-5" data-testid="enquiry-form">
              {!pkg && (
                <label className="block">
                  <span className="text-xs text-slate-400 mb-1.5 block">Package</span>
                  <select value={packageId} onChange={(e) => setPackageId(e.target.value)} data-testid="enquiry-package-select" className="input-dark">
                    <option value="">Not sure yet — surprise me</option>
                    {packages.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </label>
              )}
              <label className="block">
                <span className="text-xs text-slate-400 mb-1.5 block">Full name *</span>
                <input required value={form.name} onChange={set("name")} data-testid="enquiry-name-input" className="input-dark" placeholder="Aarav Sharma" />
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-xs text-slate-400 mb-1.5 block">Phone *</span>
                  <input required type="tel" value={form.phone} onChange={set("phone")} data-testid="enquiry-phone-input" className="input-dark" placeholder="+91 98765 43210" />
                </label>
                <label className="block">
                  <span className="text-xs text-slate-400 mb-1.5 block">Email *</span>
                  <input required type="email" value={form.email} onChange={set("email")} data-testid="enquiry-email-input" className="input-dark" placeholder="you@email.com" />
                </label>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-xs text-slate-400 mb-1.5 block">Travel month</span>
                  <input type="month" value={form.travel_date} onChange={set("travel_date")} data-testid="enquiry-date-input" className="input-dark" />
                </label>
                <label className="block">
                  <span className="text-xs text-slate-400 mb-1.5 block">Travellers</span>
                  <select value={form.travelers} onChange={set("travelers")} data-testid="enquiry-travelers-select" className="input-dark">
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </label>
              </div>
              <label className="block">
                <span className="text-xs text-slate-400 mb-1.5 block">Anything we should know?</span>
                <textarea rows={3} value={form.message} onChange={set("message")} data-testid="enquiry-message-input"
                  className="input-dark resize-none" placeholder="Honeymoon, kids, diet, pace…" />
              </label>
              <button type="submit" disabled={submitting} data-testid="enquiry-form-submit-button"
                className="w-full rounded-full bg-gold py-4 text-sm font-semibold text-onyx-900 hover:bg-gold-light transition-colors disabled:opacity-60">
                {submitting ? "Sending…" : "Send enquiry"}
              </button>
              <p className="text-[11px] text-slate-500 text-center">No payment now — we confirm availability and call you back first.</p>
            </form>
          </motion.aside>
        </div>
      )}
    </AnimatePresence>
  );
}
